import React from 'react';

import { Box, Divider, Paper, Typography } from '@mui/material';
import { isNotNil } from 'es-toolkit';
import { isEmpty } from 'es-toolkit/compat';
import { Link } from 'react-router';

import { FullscreenLoader } from '@hrlogics/react-components';
import { Field, Form, FormProps } from '@hrlogics/react-components/form';

import { appRoutes, DateFormatPatterns, RouterEntitiesId } from '@clearverify/constants';
import { formatDate } from '@clearverify/utils';

import {
  EmployersCountDocument,
  EmployersSimpleListDocument,
  EmployerUpdateMany,
  EmployerWhere,
  PeoDocument,
  PeoUpdate,
  useEmployersCountQuery,
} from 'src/api';
import { Button } from 'src/components/common';
import { SwitchField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import { usePeoById } from 'src/hooks/peo';
import { PaginationProvider } from 'src/providers/PaginationProvider';
import { SxProp } from 'src/types/mui';
import { buildUrl } from 'src/utils/router';

import { PeoEmployersLiveStatusBulkButton, PeoEmployersLiveStatusTable } from './components';
import { PeoLiveStatusFormField } from './constants';
import { PeoLiveStatusValues } from './types';

type PeoLiveStatusFormProps = {
  peoId: string;
};

export const PeoLiveStatusForm: React.FC<PeoLiveStatusFormProps> = ({ peoId }) => {
  const { createApolloContext } = useNotification();

  const { peo, updatePeoWithEmployers, loading } = usePeoById(peoId);

  const { data: employersUnderPeoData, loading: employersUnderPeoDataLoading } =
    useEmployersCountQuery({
      variables: {
        filter: {
          peoId: { equals: peoId },
        },
      },
    });
  const { data: employersIsLiveData, loading: employersIsLiveDataLoading } = useEmployersCountQuery(
    {
      variables: {
        filter: {
          peoId: { equals: peoId },
          setLiveAt: { not: { equals: null } },
        },
      },
    },
  );

  const peoSettingsLink = buildUrl(appRoutes.peos.detail.settings, {
    [RouterEntitiesId.peo]: peoId,
  });

  const initialValues: PeoLiveStatusValues = React.useMemo(() => {
    return {
      [PeoLiveStatusFormField.liveByDefault]: !!peo?.employersLiveByDefaultSetAt,
      [PeoLiveStatusFormField.setAllLive]: null,
      [PeoLiveStatusFormField.liveStatusChanges]: {},
    };
  }, [peo?.employersLiveByDefaultSetAt]);

  const onSubmit = React.useCallback<FormProps<PeoLiveStatusValues>['onSubmit']>(
    async (formValues, form) => {
      const peoUpdateData: PeoUpdate | undefined =
        formValues.liveByDefault !== initialValues.liveByDefault
          ? {
              employersLiveByDefaultSetAt: formValues.liveByDefault
                ? new Date().toISOString()
                : null,
            }
          : undefined;

      const setLiveAt = new Date().toISOString();

      const employersUpdateManyWhere: EmployerWhere[] = [];
      const employersUpdateManyData: EmployerUpdateMany[] = [];

      // The bulk change and the individual changes are mutually exclusive in the UI
      if (isNotNil(formValues.setAllLive)) {
        // All Employers under the PEO — a single updateMany scoped by the server to the PEO
        employersUpdateManyWhere.push({
          setLiveAt: formValues.setAllLive ? { equals: null } : { not: { equals: null } },
        });
        employersUpdateManyData.push({
          setLiveAt: { set: formValues.setAllLive ? setLiveAt : null },
        });
      } else if (!isEmpty(formValues[PeoLiveStatusFormField.liveStatusChanges])) {
        const liveEmployerIds: string[] = [];
        const notLiveEmployerIds: string[] = [];

        for (const [employerId, isLive] of Object.entries(
          formValues[PeoLiveStatusFormField.liveStatusChanges] ?? {},
        )) {
          (isLive ? liveEmployerIds : notLiveEmployerIds).push(employerId);
        }

        // At most two updateMany calls: one to set Live, one to unset it
        if (!isEmpty(liveEmployerIds)) {
          employersUpdateManyWhere.push({
            id: { in: liveEmployerIds },
            setLiveAt: { equals: null },
          });
          employersUpdateManyData.push({ setLiveAt: { set: setLiveAt } });
        }
        if (!isEmpty(notLiveEmployerIds)) {
          employersUpdateManyWhere.push({
            id: { in: notLiveEmployerIds },
            setLiveAt: { not: { equals: null } },
          });
          employersUpdateManyData.push({ setLiveAt: { set: null } });
        }
      }

      const hasEmployersUpdate = !isEmpty(employersUpdateManyData);

      if (!peoUpdateData && !hasEmployersUpdate) {
        return;
      }

      const successMessageText = [
        peoUpdateData ? 'PEO successfully updated' : undefined,
        hasEmployersUpdate ? 'Selected Employers have been updated successfully' : undefined,
      ]
        .filter(isNotNil)
        .join(', ');

      const refetchQueries: any[] = [{ query: PeoDocument, variables: { id: peoId } }];
      if (hasEmployersUpdate) {
        refetchQueries.push(
          {
            query: EmployersCountDocument,
            variables: {
              filter: {
                peoId: { equals: peoId },
                setLiveAt: { not: { equals: null } },
              },
            },
          },
          {
            query: EmployersSimpleListDocument,
            variables: {
              filter: {
                peoId: { equals: peoId },
              },
            },
          },
        );
      }

      try {
        await updatePeoWithEmployers(
          {
            data: peoUpdateData,
            employersUpdateManyData: hasEmployersUpdate ? employersUpdateManyData : undefined,
            employersUpdateManyWhere: hasEmployersUpdate ? employersUpdateManyWhere : undefined,
          },
          createApolloContext({ successMessage: successMessageText }),
          refetchQueries,
        );
        // The saved changes are now the baseline: clear the pending values so the bulk button
        // and the Employer switches unlock
        form.reset();
      } catch (error) {
        console.error(error);
      }
    },
    [createApolloContext, initialValues.liveByDefault, peoId, updatePeoWithEmployers],
  );

  if (loading || employersUnderPeoDataLoading || employersIsLiveDataLoading) {
    return <FullscreenLoader />;
  }

  return (
    <Form
      initialValues={initialValues}
      onSubmit={onSubmit}
      subscription={{ submitting: true, dirty: true }}
    >
      {({ form, submitting, dirty }) => {
        return (
          <React.Fragment>
            <Paper variant="outlined" sx={paperStyles} elevation={2}>
              <Box sx={checkboxSettingsStyles}>
                <Box>
                  <Typography variant="subtitle1">
                    Set All New Employers Live upon creation{' '}
                    {peo?.employersLiveByDefaultSetAt
                      ? `(set at ${formatDate(peo.employersLiveByDefaultSetAt, DateFormatPatterns.shortDateWithSlash)})`
                      : null}
                  </Typography>

                  <Typography variant="body1" color="text.secondary" mt={1}>
                    Activate this toggle if all Employers that will be added in the future under
                    this PEO should instantly be set Live
                  </Typography>
                </Box>
                <Box>
                  <Field
                    name={PeoLiveStatusFormField.liveByDefault}
                    component={SwitchField}
                    disabled={submitting}
                  />
                </Box>
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ ...paperStyles, mt: 2 }} elevation={2}>
              <Box sx={checkboxSettingsStyles}>
                <Box>
                  <Typography variant="subtitle1">All Employers under PEO</Typography>

                  <Typography variant="body1" color="text.secondary" mt={1}>
                    Set every Employer under this PEO Live or Not Live at once. Individual Employer
                    changes below and the bulk change cannot be combined
                  </Typography>
                </Box>
                <Box>
                  <PeoEmployersLiveStatusBulkButton
                    employersCount={employersUnderPeoData?.employerCount ?? 0}
                    liveEmployersCount={employersIsLiveData?.employerCount ?? 0}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2, textTransform: 'uppercase' }}>Or</Divider>

              <Box mt={2}>
                <PaginationProvider>
                  <PeoEmployersLiveStatusTable peoId={peoId} />
                </PaginationProvider>
              </Box>
            </Paper>

            <Box display="flex" gap={3} justifyContent="center" mt={2.5}>
              <Button
                component={Link}
                type="button"
                variant="outlined"
                to={peoSettingsLink}
                loading={submitting}
                disabled={submitting}
              >
                Go back
              </Button>
              <Button
                type="reset"
                variant="outlined"
                loading={submitting}
                disabled={submitting || !dirty}
                onClick={() => form.reset()}
                color="error"
              >
                Reset
              </Button>
              <Button
                type="submit"
                variant="contained"
                loading={submitting}
                disabled={submitting || !dirty}
              >
                Save Changes
              </Button>
            </Box>
          </React.Fragment>
        );
      }}
    </Form>
  );
};

const checkboxSettingsStyles: SxProp = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
};

const paperStyles: SxProp = {
  padding: 2,
};
